'use client';

import { Button, cn } from '@shared/components/shadcn';
import { Error, Minus, Plus } from '@shared/icons';
import { type ChangeEvent, type FocusEvent, useId, useState } from 'react';

type StepperInputSize = 'large' | 'small';

type StepperInputProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  label?: string;
  helpMessage?: string;
  hasError?: boolean;
  disabled?: boolean;
  size?: StepperInputSize;
  quickIncrements?: number[];
  decrementLabel?: string;
  incrementLabel?: string;
  quickIncrementLabel?: (amount: number) => string;
  id?: string;
  'data-testid'?: string;
};

const DIGITS_ONLY = /^\d+$/;

const SIZE_STYLES = {
  large: {
    wrapperGap: 'gap-2',
    pill: 'h-12',
    buttonSize: 'icon-m',
    iconSize: 'small',
  },
  small: {
    wrapperGap: 'gap-1',
    pill: 'h-8',
    buttonSize: 'icon-xs',
    iconSize: 'extra-small',
  },
} as const;

function parseQuantity(rawValue: string, min: number): number | null {
  if (!DIGITS_ONLY.test(rawValue)) {
    return null;
  }

  const parsed = Number.parseInt(rawValue, 10);
  return parsed >= min ? parsed : null;
}

/**
 * A component that allows the user to increment and decrement a quantity.
 * @param value - The current value of the quantity.
 * @param onChange - A function that will be called when the value changes.
 * @param min - The minimum value of the quantity.
 * @param label - The label to display above the quantity.
 * @param helpMessage - The helper message to display below the quantity.
 * @param hasError - Whether the quantity has an error.
 * @param disabled - Whether the quantity is disabled.
 * @param size - The size of the quantity.
 * @param quickIncrements - An array of numbers that will be used to increment the quantity. Shown while the input is focused.
 * @param decrementLabel - The label to display for the decrement button.
 * @param incrementLabel - The label to display for the increment button.
 * @param quickIncrementLabel - A function that will be called to get the label for a quick increment button.
 * @param id - The id to use for the component.
 * @param 'data-testid' - The data-testid to use for the component.
 */
function StepperInput({
  value,
  onChange,
  min = 1,
  label,
  helpMessage,
  hasError = false,
  disabled = false,
  size = 'large',
  quickIncrements,
  decrementLabel = 'Decrease quantity',
  incrementLabel = 'Increase quantity',
  quickIncrementLabel = amount => `Add ${amount}`,
  id,
  'data-testid': dataTestId = 'stepper-input',
}: StepperInputProps) {
  const generatedId = useId();
  const inputId = id ?? `${generatedId}-field`;
  const helpMessageId = `${inputId}-help`;
  const [draft, setDraft] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const displayValue = draft ?? String(value);
  const isDecrementDisabled = disabled || value <= min;
  const sizeStyles = SIZE_STYLES[size];

  function commitValue(nextValue: number) {
    setDraft(null);
    if (nextValue !== value) {
      onChange(nextValue);
    }
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const nextRawValue = event.target.value;
    if (nextRawValue !== '' && !DIGITS_ONLY.test(nextRawValue)) {
      return;
    }

    setDraft(nextRawValue);
    const parsed = parseQuantity(nextRawValue, min);
    if (parsed !== null) {
      onChange(parsed);
    }
  }

  function handleInputBlur(event: FocusEvent<HTMLInputElement>) {
    const parsed = parseQuantity(event.target.value, min);
    if (parsed === null) {
      setDraft(null);
    } else {
      commitValue(parsed);
    }

    setIsInputFocused(false);
  }

  return (
    <div className={cn('flex w-full flex-col', sizeStyles.wrapperGap, disabled && 'pointer-events-none opacity-50')} data-testid={dataTestId}>
      {label ? (
        <label htmlFor={inputId} className="paragraph-explanatory text-foreground" data-testid={`${dataTestId}-label`}>
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'flex w-full items-center justify-between rounded-full border bg-background px-1',
          sizeStyles.pill,
          hasError ? 'border-error-foreground' : 'border-border-hover',
          !hasError && !disabled && 'hover:border-border-active focus-within:border-border-active'
        )}
        data-testid={`${dataTestId}-control`}
      >
        <Button
          type="button"
          variant="primary"
          size={sizeStyles.buttonSize}
          className="shrink-0"
          onClick={() => commitValue(value - 1)}
          disabled={isDecrementDisabled}
          aria-label={decrementLabel}
          data-testid={`${dataTestId}-decrement`}
        >
          <Minus size={sizeStyles.iconSize} />
        </Button>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          enterKeyHint="done"
          autoComplete="off"
          aria-label={label}
          aria-invalid={hasError || undefined}
          aria-describedby={helpMessage ? helpMessageId : undefined}
          disabled={disabled}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsInputFocused(true)}
          onBlur={handleInputBlur}
          onKeyDown={event => event.key === 'Enter' && event.currentTarget?.blur()}
          className="paragraph h-full min-w-0 flex-1 bg-transparent text-center outline-none disabled:cursor-not-allowed"
          data-testid={`${dataTestId}-input`}
        />
        <Button
          type="button"
          variant="primary"
          size={sizeStyles.buttonSize}
          className="shrink-0"
          onClick={() => commitValue(value + 1)}
          disabled={disabled}
          aria-label={incrementLabel}
          data-testid={`${dataTestId}-increment`}
        >
          <Plus size={sizeStyles.iconSize} />
        </Button>
      </div>
      {helpMessage ? (
        <p
          id={helpMessageId}
          className={cn('flex items-center gap-2 paragraph-s', hasError ? 'text-error-foreground' : 'text-foreground')}
          role={hasError ? 'alert' : 'status'}
          data-testid={`${dataTestId}-help`}
        >
          {hasError ? <Error size="tiny" className="shrink-0 text-error-foreground" aria-hidden /> : null}
          {helpMessage}
        </p>
      ) : null}
      {isInputFocused && !disabled && quickIncrements && quickIncrements.length > 0 ? (
        <div className="flex w-full items-stretch gap-2" data-testid={`${dataTestId}-quick-increments`}>
          {quickIncrements.map(amount => (
            <button
              key={amount}
              type="button"
              className="paragraph-s flex min-w-16 flex-1 items-center justify-center rounded-md bg-container p-4 text-foreground focus-visible:outline-none focus-visible:ring-brand focus-visible:ring-[3px]"
              onMouseDown={event => event.preventDefault()}
              onClick={() => commitValue(value + amount)}
              aria-label={quickIncrementLabel(amount)}
              data-testid={`${dataTestId}-quick-increment-${amount}`}
            >
              +{amount}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export { StepperInput, type StepperInputProps, type StepperInputSize };
